import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { FiArrowLeft, FiZap } from 'react-icons/fi';
import './PowerHourPage.css';

// Every NUMBER on this page comes from GET /rules, which is generated from
// live config — the narrative is hand-written, the facts are not. This page
// used to hardcode both, and it lied: it described three bonus windows while
// the live config had two, both disabled.
interface RuleMult { kind: string; name: string; days_label: string; start: string; end: string; multiplier: number; }
interface RuleItem { key: string; code: string; redeem_command: string; label: string; coins: number; teams: string[]; requires_approval: boolean; poolable: boolean; allowed_days_label: string; blackout_power_hours: boolean; eligible_days: number[]; stock_per_month: number; }
interface Rules {
  season: string; season_ends: string; week_champion_min_meetings: number;
  earning: Record<string, number>;
  multipliers: RuleMult[];
  catalog: RuleItem[];
  prizes: Record<string, string>;
  bonus_rules: { name: string; metric: string; count: number; period: string; reward: string; auto_approve: boolean }[];
  milestones: { period: string; items: { meetings: number; reward: string }[] };
  marketplace: { enabled: boolean; approver: string };
  wheel?: { enabled: boolean; per_rep_per_week: number; prices: Record<string, number>;
    outcomes: { key: string; label: string; probability_pct: number }[] };
  boosts?: { enabled: boolean; multiplier: number; max_total_multiplier: number;
    per_rep_per_week: number; fuel_per_dial: number; fuel_per_connect: number };
}

const fmtWindow = (m: RuleMult) =>
  `${m.name} (${m.days_label} ${m.start}–${m.end}, ${m.multiplier}x)`;

const PowerHourGuide: React.FC = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState<Rules | null>(null);
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/power-hour/rules`)
      .then(r => r.json()).then(setRules).catch(() => {});
  }, []);
  const meetingCoins = rules?.earning?.coins_per_meeting ?? 500;
  const power = rules?.multipliers.find(m => m.kind === 'power_hour');
  const bonusHours = rules?.multipliers.filter(m => m.kind === 'bonus_hours') ?? [];
  return (
    <div className="ph-page ph-guide">
      <div className="ph-topbar">
        <div className="ph-logo">
          <button className="ph-icon-btn" onClick={() => navigate('/power-hour')} title="Back">
            <FiArrowLeft />
          </button>
          <FiZap /> HOW EARN THE SUMMIT WORKS
        </div>
      </div>

      <div className="ph-guide-grid">
        <div className="ph-cfg-card">
          <h3>🎯 The one rule</h3>
          <p><b>Coins crown the winner</b> — and <b>meetings are the only thing that mints
            them</b>: a booked meeting is worth <b>{meetingCoins.toLocaleString()} 🪙</b>, connects and dials are worth
            <b>0</b>. Dials and connects still show on every board as activity — they prove the
            work — but you can't out-dial or out-connect anyone: booking is the board. Every
            board ranks by coins, so the leader you watch climb all day is the one who wins.</p>
          <p className="ph-guide-dim">Data flows live from the Nooks dialer (seconds after each hang-up).
            A rep books a meeting by picking the <b>Meeting Booked</b> disposition in Nooks — no manual entry, no honor system.</p>
          <p><b>What counts as a meeting</b>: a <b>net-new booking from a cold call</b> — an
            outbound Nooks call of <b>60 seconds or more</b> to a number <b>nobody has booked
            before</b>. Rebooking a prospect (after a no-show, a reschedule, or logging the same
            booking twice), a booking on an inbound call, or a disposition on a call that never
            connected still logs in Nooks, but it earns no coins and doesn't move a board, a
            streak, or a hat trick.</p>
        </div>

        <div className="ph-cfg-card">
          <h3>⚡ Power Hours</h3>
          <p>Scheduled rally blocks{power
            ? <> — currently <b>{power.days_label} {power.start}–{power.end}</b></>
            : ''}.
            During a power hour your <b>meeting coins are multiplied</b>
            {power ? <> (<b>{power.multiplier}x</b>)</> : ''} —
            multipliers only ever boost outcomes, never dials — the TV switches to the live block
            leaderboard automatically, and Slack gets a 30-minute warning, a go-live post, and a
            final summary with the podium.</p>
          <p className="ph-guide-dim">Anyone can also host an ad-hoc session from the lobby —
            the TV picks it up too, then returns to the dashboard when it ends.</p>
        </div>

        <div className="ph-cfg-card">
          <h3>🚀 Bonus Hours</h3>
          {bonusHours.length > 0 ? (
            <p>Off-hours incentives, live right now: {bonusHours.map((m, i) => (
              <React.Fragment key={m.name}>{i > 0 && ' · '}<b>{fmtWindow(m)}</b></React.Fragment>
            ))}. During a bonus hour, <b>booked meetings</b> earn multiplied coins — dials and
            connects don't, so spraying voicemails at midnight earns nothing.</p>
          ) : (
            <p className="ph-guide-dim">No bonus hours are scheduled right now — when a leader
              enables one in ⚙️ Config, it appears here automatically with its days and multiplier.</p>
          )}
        </div>

        <div className="ph-cfg-card">
          <h3>🔥 Streaks</h3>
          <p>Book <b>≥1 meeting in a day</b> and your streak grows — dials don't count toward
            streaks. Skip a weekday with no meeting and it resets. <b>Weekends never break a
            streak</b> — and a weekend meeting extends it. Flames (🔥7) show next to names on every board.</p>
          <p><b>Streak rewards</b>: every N days (configurable, default 5) the streak holder
            gets a shout-out + the reward you set in Config. <b>Gentle nudges</b>: mid-afternoon,
            anyone with a streak on the line gets a Slack heads-up with exactly what keeps it alive.</p>
        </div>

        <div className="ph-cfg-card">
          <h3>🤖 It runs itself</h3>
          <p>The engagement engine posts to Slack on its own, only when the data warrants it:</p>
          <p>• <b>Power hours</b>: 30-min warning → go-live → final summary with the podium<br />
            • <b>1 PM</b>: photo-finish race alerts ("next booking takes the day") or
            milestone proximity ("2 meetings from the golf flag 🚩")<br />
            • <b>3 PM</b>: streak-at-risk nudges<br />
            • <b>5 PM</b>: 🏆 daily winner · Fridays the weekly · month-end the monthly,
            including the ⚔️ team battle result</p>
          <p className="ph-guide-dim">Every crowned winner is saved to the awards history
            (the 🏅 Hall of Fame on the TV), so quarter-end "who won the most days" is one query away.
            History starts the day awards go live — it isn't backfilled.</p>
        </div>

        <div className="ph-cfg-card">
          <h3>⚔️ Team Battle</h3>
          <p>Teams (any names, set in Config) race on <b>meetings</b> over the milestone
            period. Only reps who've dialed recently count — execs and ops don't drag a team down.
            The score lives on the TV; the winner is crowned in the period's
            5 PM results post with the battle prize you configure (e.g. "losing team buys lunch").</p>
        </div>

        <div className="ph-cfg-card">
          <h3>🎁 Prizes (set in ⚙️ Config)</h3>
          <p><b>🏆 CHAMPION</b> — most coins in the day / week / month. The crown. Meetings
            are the only coin source, so this is the best booker boosted by multipliers and
            jackpots — booking during power hours is how you top it.
            {rules && Object.keys(rules.prizes).length > 0 && (
              <> Current prizes: {Object.entries(rules.prizes)
                .map(([p, v]) => `${p} → ${v}`).join(' · ')}.</>
            )}</p>
          <p className="ph-guide-dim">The <b>weekly</b> crown has a floor: you need at least
            <b> {rules?.week_champion_min_meetings ?? 3} meetings booked</b> to take it. Fall
            short and the week has <b>no champion at all</b> — the crown isn't passed down to
            second place. Weekly and monthly prizes <b>send automatically</b> the moment
            they're won; the daily prize waits for a quick human OK.</p>
          <p><b>🎯 TOP CLOSER</b> — most meetings booked. A shout-out (no separate prize) that
            only fires when the top booker <i>isn't</i> already the Champion — a nod to
            whoever got edged out on multipliers.</p>
          <p><b>🏔️ Team Reward Mountain</b> — the whole team climbs one mountain together;
            flags on the slope are the rewards a leader configures{rules &&
              rules.milestones.items.length > 0 && (
              <> — right now {rules.milestones.items
                .map(m => `${m.meetings} meetings this ${rules.milestones.period} → ${m.reward}`)
                .join(', ')}</>
            )}. Hit the flag, everyone gets it.</p>
        </div>

        <div className="ph-cfg-card">
          <h3>🎰 Bonus Coins (one-off awards)</h3>
          <p>From <b>⚙️ Config → 🎰 Award Bonus Coins</b>, a leader can drop any number of
            coins on any person — a spot reward for a clutch save, a great demo, a team assist.
            The coins <b>count toward that rep's leaderboard and prize standings</b> just like a
            booked meeting, so a big award can shake up who's winning the day.</p>
          <p className="ph-guide-dim">Every award fires a <b>JACKPOT</b> in the celebrations
            Slack channels (@mentioning the rep) and a <b>coins-falling slot-machine animation</b>
            on the TV. Use it live — it lands best when the floor is watching the board.</p>
        </div>

        <div className="ph-cfg-card">
          <h3>💳 Instant Gift Cards</h3>
          <p>From <b>⚙️ Config → 💳 Issue Gift Card</b>, a leader can send a <b>real gift
            card</b> to anyone on the spot — pick the person, the dollar amount, and hit send.
            The rep gets a <b>reward link by email</b> and chooses their own card
            (DoorDash, Amazon, Visa…). No waiting on a period to end, no approval queue —
            entering the PIN is the approval.</p>
          <p className="ph-guide-dim">It fires a <b>GIFT CARD JACKPOT</b> in the celebrations
            channels and on the TV, and every send is logged in the payouts history.</p>
        </div>

        <div className="ph-cfg-card">
          <h3>🏅 Prize Rules (automatic feats)</h3>
          <p>Set standing challenges in <b>⚙️ Config → 🏅 Prize Rules</b> — for example
            <b> 3 meetings in 1 day = $100 gift card</b> (the 🎩 Hat Trick). Meeting rules count
            only net-new cold-call bookings — see <i>What counts as a meeting</i> above. The
            moment a rep crosses the line,
            the prize fires: a <b>PRIZE UNLOCKED</b> celebration in Slack (@mentioning the rep)
            and on the TV. Rules can watch <b>meetings, connects, dials, or coins</b> over a
            day, week, or month.</p>
          <p className="ph-guide-dim">Each rule pays <b>once per rep per period</b> — hit it
            Tuesday and it re-arms Wednesday. A rule marked <b>auto-pay</b> sends the gift card
            the moment the feat lands (the numbers already prove it); anything else queues in
            💸 Prize Payouts for one-click approval.
            {rules && rules.bonus_rules.length > 0 && (
              <> Live now: {rules.bonus_rules.map(b =>
                `${b.name} (${b.count} ${b.metric}/${b.period} → ${b.reward}${b.auto_approve ? ', auto-pays' : ''})`)
                .join(' · ')}.</>
            )}</p>
        </div>

        <div className="ph-cfg-card">
          <h3>🛍️ The Vault (spend your coins in Slack)</h3>
          <p>Your coins aren't just a score — they're a <b>wallet</b>. In Slack:
            <b> /coins</b> shows your quarterly balance, <b>/coins how</b> the full live rules,
            <b> /coins shop</b> the catalog your team can redeem, and
            <b> /coins redeem &lt;code&gt;</b> spends them — short codes like <b>wfh</b>,
            <b> early</b>, <b>late</b>; <b>/coins shop</b> prints the exact command under every reward. The price goes on hold and
            the approver gets a DM with ✅/❌ buttons naming your day and who else is out then.
            You're DMed the verdict; a deny returns every coin.</p>
          {rules && rules.catalog.length > 0 && (
            <p className="ph-guide-dim">On the shelves now: {rules.catalog
              .filter(c => !c.poolable)
              .map(c => `${c.label} — \`${c.redeem_command}\` (${c.coins.toLocaleString()} 🪙${
                c.blackout_power_hours || c.eligible_days.length
                  ? `, ${c.allowed_days_label} only` : ''}${
                c.teams.length ? `, ${c.teams.join('/')}` : ''})`)
              .join(' · ')}. Prices differ by team on purpose — they're pinned to what each
              team actually earns, so a reward costs the same effort everywhere.</p>
          )}
          {rules?.boosts?.enabled && (
            <p><b>⛽ Fuel & 🚀 boosts</b> — dials and connects mint <b>Fuel</b>
              ({rules.boosts.fuel_per_dial}/dial, {rules.boosts.fuel_per_connect}/connect,
              resets Monday), a second currency that buys nothing but <b>personal boosts</b>:
              <b> /coins boost</b> goes {rules.boosts.multiplier}x on your own meeting coins,
              stacks with power hours up to <b>{rules.boosts.max_total_multiplier}x</b>, max{' '}
              {rules.boosts.per_rep_per_week}/week — and it announces itself to the room.
              Boosting never touches your coin wallet. Your ⛽ Fuel shows next to your coins on
              the TV board. Watch for surprise ⚡ <b>Fuel Rushes</b> when the floor goes quiet.</p>
          )}
          {rules?.wheel?.enabled && (
            <p><b>🎡 Mystery Wheel</b> — <b>/coins wheel</b> shows the full published odds,
              <b> /coins wheel spin</b> takes the gamble ({Object.entries(rules.wheel.prices)
                .map(([t, p]) => `${t} ${p.toLocaleString()} 🪙`).join(' · ')},{' '}
              {rules.wheel.per_rep_per_week}/week). Every prize is <b>work ammunition</b> —
              Fuel, a free boost, a MEGA 3x, or a ⚡ <b>Fuel Rush for the whole floor</b> with
              your name on the announcement. <b>Never coins back</b> and <b>never time-off</b>:
              the wheel converts perk savings into dials, and pays for itself through the
              meetings you book with what it gives you. The table:{' '}
              {rules.wheel.outcomes.map((o, i) => (
                <React.Fragment key={o.key}>{i > 0 && ' · '}
                  <b>{o.probability_pct}%</b> {o.label}
                </React.Fragment>
              ))}. Run <b>/coins wheel</b> for what each slice means in your own numbers.
              Every roll is recorded on the ledger, so any spin can be re-checked by hand.
              Every spin (and every Vault redemption) gets a shout-out in Slack and on the TV.</p>
          )}
          <p>Some rewards are <b>team pools</b>: <b>/coins pool</b> shows open pots and
            <b> /coins pool &lt;item&gt; &lt;amount&gt;</b> chips in. Fill the pot together and
            the whole team gets it.</p>
          <p className="ph-guide-dim"><b>Wallets are quarterly</b> — spendable = coins earned
            this quarter minus what you've spent; unspent coins expire at quarter end. Your
            <b> career total and the leaderboards never go down</b> when you redeem. Catalog,
            prices, and stock live in ⚙️ Config → 🛍️ The Vault (roll-out may start with a
            pilot group).</p>
        </div>

        <div className="ph-cfg-card">
          <h3>🖥️ Running it (sales leader cheat-sheet)</h3>
          <p>1. <b>⚙️ Config</b> — PIN-protected (leaders only). Set prizes, milestones,
            power hour times, team rosters, Slack channels. One save.</p>
          <p>2. <b>📺 TV Mode</b> — put it fullscreen on the floor TV and leave it. It handles
            countdowns, power hours, and the dashboard by itself.</p>
          <p>3. <b>Point at the board.</b> Award the prizes you configured — winners are
            unambiguous and webhook-verified.</p>
          <p className="ph-guide-dim">Status dot on boards: 🟢 realtime feed healthy ·
            🟡 no deliveries in 30 min · 🔵 fallback (Gong) only.</p>
        </div>
      </div>
    </div>
  );
};

export default PowerHourGuide;
